import ComponentLoader from '@common/components/ComponentLoader';
import { useIntersectionObserver } from '@common/hooks/useIntersectionObserver';
import DeliveryEmpty from '@console/components/project/delivery/Empty';
import type {
  DeliveryPropsMappedFromDispatch,
  DeliveryPropsMappedFromState
} from '@console/containers/project/DeliveryContainer';
import DeliveryTableContainer from '@console/containers/project/delivery/TableContainer';
import type { LocalBulkDeliverable } from '@console/reducers/projects';
import type { StyleRules, WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router';

const styles = (): StyleRules => ({});

type DeliveryProps = DeliveryPropsMappedFromState &
  DeliveryPropsMappedFromDispatch &
  WithStyles<typeof styles>;

type PathParams = {
  urlCode?: string;
};

const Delivery: React.FC<DeliveryProps> = ({
  bulkDeliverables,
  getProjectInitialBulkDeliverables,
  getProjectContinuousBulkDeliverables,
  isBulkDeliverablesLoading
}) => {
  const params = useParams<PathParams>();
  const infiniteScrollTarget = useRef<HTMLDivElement>(null);
  const { startObserving, isIntersect } =
    useIntersectionObserver(infiniteScrollTarget);

  useEffect(() => {
    if (params.urlCode) {
      getProjectInitialBulkDeliverables(params.urlCode);
      startObserving(true);
    }
  }, []);

  useEffect(() => {
    if (params.urlCode && isIntersect) {
      getProjectContinuousBulkDeliverables(params.urlCode);
    }
  }, [isIntersect]);

  const renderContents = (): ReactElement<any> => {
    if (!!bulkDeliverables && bulkDeliverables.length !== 0) {
      return renderTable(bulkDeliverables);
    } else if (isBulkDeliverablesLoading) {
      return <ComponentLoader isLoading={isBulkDeliverablesLoading} />;
    }
    return renderEmpty();
  };

  const renderTable = (
    bulkDeliverables: LocalBulkDeliverable[]
  ): ReactElement<any> => (
    <>
      <DeliveryTableContainer bulkDeliverables={bulkDeliverables} />
      <ComponentLoader isLoading={isBulkDeliverablesLoading} />
    </>
  );

  const renderEmpty = (): ReactElement<any> => <DeliveryEmpty />;

  return (
    <section>
      {renderContents()}
      <div ref={infiniteScrollTarget} />
    </section>
  );
};

export default withStyles(styles)(Delivery);
